import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { dummyP2PFiatCurrencies } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./P2PFiatManagementPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso || "N/A";
  }
};

const P2PFiatManagementPage = () => {
  const [currencies, setCurrencies] = useState(dummyP2PFiatCurrencies);
  const [statusFilter, setStatusFilter] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ currencyName: "", shortName: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ currencyName: "", shortName: "" });

  const filtered = useMemo(() => {
    if (statusFilter === "All") return currencies;
    return currencies.filter((c) => (c.status || "").toUpperCase() === statusFilter.toUpperCase());
  }, [currencies, statusFilter]);

  const counts = useMemo(() => ({
    total: currencies.length,
    active: currencies.filter((c) => (c.status || "").toUpperCase() === "ACTIVE").length,
    inactive: currencies.filter((c) => (c.status || "").toUpperCase() === "INACTIVE").length,
  }), [currencies]);

  const resetFilters = () => setStatusFilter("All");

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newCurrency.currencyName.trim() || !newCurrency.shortName.trim()) return;
    setCurrencies((prev) => [
      ...prev,
      {
        _id: "fiat_" + Date.now(),
        currencyName: newCurrency.currencyName.trim(),
        shortName: newCurrency.shortName.trim().toUpperCase(),
        status: "ACTIVE",
        createdAt: new Date().toISOString(),
      },
    ]);
    setNewCurrency({ currencyName: "", shortName: "" });
    setShowAddModal(false);
  };

  const handleToggleStatus = (id) => {
    setCurrencies((prev) =>
      prev.map((c) =>
        c._id === id
          ? { ...c, status: (c.status || "").toUpperCase() === "ACTIVE" ? "INACTIVE" : "ACTIVE" }
          : c
      )
    );
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this fiat currency?")) {
      setCurrencies((prev) => prev.filter((c) => c._id !== id));
    }
  };

  const handleEditClick = (row) => {
    setEditingId(row._id);
    setEditForm({ currencyName: row.currencyName || "", shortName: row.shortName || "" });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingId || !editForm.currencyName.trim() || !editForm.shortName.trim()) return;
    setCurrencies((prev) =>
      prev.map((c) =>
        c._id === editingId
          ? { ...c, currencyName: editForm.currencyName.trim(), shortName: editForm.shortName.trim().toUpperCase() }
          : c
      )
    );
    setEditingId(null);
    setEditForm({ currencyName: "", shortName: "" });
  };

  return (
    <main className="dashboard-main dashboard-subpage p2p-fiat-page">
      <section className="dashboard-hero p2p-fiat-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Fiat Currency</span>
          </p>
          <h1 className="dashboard-hero__title">P2P Fiat Currency Management</h1>
          <p className="dashboard-hero__subtitle">Manage fiat currencies for P2P trading.</p>
        </div>
        <div className="p2p-fiat-hero__icon">
          <i className="fas fa-dollar-sign" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="p2p-fiat-stats">
          <div className="p2p-fiat-stat-card">
            <i className="fas fa-list p2p-fiat-stat-card__icon p2p-fiat-stat-card__icon--default" />
            <span className="p2p-fiat-stat-card__value">{counts.total}</span>
            <span className="p2p-fiat-stat-card__label">Total Currencies</span>
          </div>
          <div className="p2p-fiat-stat-card">
            <i className="fas fa-check-circle p2p-fiat-stat-card__icon p2p-fiat-stat-card__icon--active" />
            <span className="p2p-fiat-stat-card__value">{counts.active}</span>
            <span className="p2p-fiat-stat-card__label">Active</span>
          </div>
          <div className="p2p-fiat-stat-card">
            <i className="fas fa-times-circle p2p-fiat-stat-card__icon p2p-fiat-stat-card__icon--inactive" />
            <span className="p2p-fiat-stat-card__value">{counts.inactive}</span>
            <span className="p2p-fiat-stat-card__label">Inactive</span>
          </div>
        </div>

        <section className="p2p-fiat-filters-section">
          <div className="p2p-fiat-filters-header">
            <h2 className="p2p-fiat-filters__title"><i className="fas fa-filter" /> Filters</h2>
            <button type="button" className="p2p-fiat-btn-add" onClick={() => setShowAddModal(true)}>
              <i className="fas fa-plus" /> Add New Fiat
            </button>
          </div>
          <div className="p2p-fiat-filters-row">
            <div className="p2p-fiat-filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <button type="button" className="p2p-fiat-btn-reset" onClick={resetFilters}>
              <i className="fas fa-sync-alt" /> Reset Filters
            </button>
          </div>
        </section>

        <section className="p2p-fiat-list-section">
          <h2 className="p2p-fiat-list__title"><i className="fas fa-th-list" /> Fiat Currencies List ({filtered.length})</h2>
          <div className="p2p-fiat-table-wrap">
            <table className="p2p-fiat-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>CURRENCY NAME</th>
                  <th>SHORT NAME</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>{row.currencyName}</td>
                    <td>{row.shortName}</td>
                    <td><span className={`p2p-fiat-status-badge p2p-fiat-status-badge--${(row.status || "").toLowerCase()}`}>{row.status}</span></td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <div className="p2p-fiat-actions">
                        <button type="button" className="p2p-fiat-action-btn p2p-fiat-action-btn--edit" onClick={() => handleEditClick(row)}>Edit</button>
                        {(row.status || "").toUpperCase() === "ACTIVE" ? (
                          <button type="button" className="p2p-fiat-action-btn p2p-fiat-action-btn--deactivate" onClick={() => handleToggleStatus(row._id)}>Deactivate</button>
                        ) : (
                          <button type="button" className="p2p-fiat-action-btn p2p-fiat-action-btn--activate" onClick={() => handleToggleStatus(row._id)}>Activate</button>
                        )}
                        <button type="button" className="p2p-fiat-action-btn p2p-fiat-action-btn--delete" onClick={() => handleDelete(row._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {showAddModal && (
        <div className="p2p-fiat-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="p2p-fiat-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="p2p-fiat-modal__title">Add New Fiat Currency</h3>
            <form onSubmit={handleAdd}>
              <div className="p2p-fiat-modal__group">
                <label>Currency Name</label>
                <input type="text" placeholder="e.g. US Dollar" value={newCurrency.currencyName} onChange={(e) => setNewCurrency((n) => ({ ...n, currencyName: e.target.value }))} required />
              </div>
              <div className="p2p-fiat-modal__group">
                <label>Short Name</label>
                <input type="text" placeholder="e.g. USD" value={newCurrency.shortName} onChange={(e) => setNewCurrency((n) => ({ ...n, shortName: e.target.value }))} maxLength={5} required />
              </div>
              <div className="p2p-fiat-modal__actions">
                <button type="button" className="p2p-fiat-modal-btn p2p-fiat-modal-btn--cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="p2p-fiat-modal-btn p2p-fiat-modal-btn--submit">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingId && (
        <div className="p2p-fiat-modal-overlay" onClick={() => setEditingId(null)}>
          <div className="p2p-fiat-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="p2p-fiat-modal__title">Edit Fiat Currency</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="p2p-fiat-modal__group">
                <label>Currency Name</label>
                <input type="text" placeholder="e.g. US Dollar" value={editForm.currencyName} onChange={(e) => setEditForm((f) => ({ ...f, currencyName: e.target.value }))} required />
              </div>
              <div className="p2p-fiat-modal__group">
                <label>Short Name</label>
                <input type="text" placeholder="e.g. USD" value={editForm.shortName} onChange={(e) => setEditForm((f) => ({ ...f, shortName: e.target.value }))} maxLength={5} required />
              </div>
              <div className="p2p-fiat-modal__actions">
                <button type="button" className="p2p-fiat-modal-btn p2p-fiat-modal-btn--cancel" onClick={() => setEditingId(null)}>Cancel</button>
                <button type="submit" className="p2p-fiat-modal-btn p2p-fiat-modal-btn--submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default P2PFiatManagementPage;
